import type { Poll, PollResponse } from '@tunasong/schemas'
import { getProgress } from './progress-util.js'

export const moveChoicesToTop = (indices: number[], choices: Record<number, boolean>) => {
  const topIndices = indices.filter(idx => choices[idx])
  const bottomIndices = indices.filter(idx => !choices[idx])

  return [...topIndices, ...bottomIndices]
}

/** Park–Miller minimal standard generator: modulus 2^31 - 1, multiplier 48271. Plain arithmetic, no bitwise ops. */
const modulus = 2147483647

const seedState = (seed: string) => {
  const hash = [...seed].reduce((state, char) => (state * 31 + char.charCodeAt(0)) % modulus, 7)
  return hash === 0 ? 1 : hash
}

/**
 * A shuffle that is a pure function of `seed`. It used to call Math.random during render, so the order changed
 * whenever the poll or its responses re-rendered — right after a vote, under the voter's cursor. GH #1968
 */
const seededShuffle = (length: number, seed: string) => {
  const indices = [...Array(length).keys()]
  let state = seedState(seed)
  for (let i = indices.length - 1; i > 0; i--) {
    state = (state * 48271) % modulus
    const j = Math.floor((state / modulus) * (i + 1))
    ;[indices[i], indices[j]] = [indices[j], indices[i]]
  }
  return indices
}

/**
 * Option indices in display order. `random` is seeded (use viewer + poll, so each voter keeps one order).
 * Sorting by votes breaks ties by stored index in both directions: descending used to be ascending-then-reverse,
 * which put tied options in reverse order and made a lightly voted poll look shuffled. GH #1968
 */
export const sortPollOptionsIndices = (
  poll: Poll,
  responses: PollResponse[],
  order: 'random' | 'desc' | 'asc' = 'random',
  seed = ''
) => {
  if (order === 'random') {
    return seededShuffle(poll.pollOptions.length, seed)
  }
  const progress = poll.pollOptions.map((_, idx) => getProgress(poll, responses, idx))
  const direction = order === 'asc' ? 1 : -1

  return poll.pollOptions
    .map((_, idx) => idx)
    .sort((a, b) => (progress[a] === progress[b] ? a - b : (progress[a] - progress[b]) * direction))
}

export const sortPollOptions = (poll: Poll, responses: PollResponse[], order: 'desc' | 'asc' = 'desc') => {
  const sortedIndices = sortPollOptionsIndices(poll, responses, order)
  return sortedIndices.map(idx => poll.pollOptions[idx])
}
